import { useState } from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  formatTargetMetricValue,
  type ProgramTargetProjection,
  type ProjectedTarget,
  type TargetItem,
  type TargetMetric,
  type UnitsPreference,
  programRoundLabel,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { Chip } from "../primitives/Chip";
import { ZONE_GLYPH, zoneTextStyle, zoneWord } from "../primitives/TargetBar";
import { weightUnitLabels } from "../weightUnitLabels";
import { stateAria } from "../a11y";

// AP-10 — the day×week projection matrix under the weekly-targets editor. Rows =
// targets; for the selected cycle-week, columns are the training days (D1…Dn),
// then that week's total scored against the band, then the W1→Wn ramp. It turns
// "you set a goal" into "here's how the program actually delivers it, day by day
// and across the cycle", so the athlete knows whether to touch reps or %.
//
// Pure presentation over `programTargetProjection`'s data — no metric math here.
// Zone is glyph + word + colour (never colour alone; [[feedback_user_visual_
// accessibility]]). On a narrow viewport the per-day columns fold away: each row
// shows total + ramp, and a tap expands that row's days — the totals are what the
// athlete reads most, so they stay; the days are on demand. Every row exposes its
// full breakdown (days + total + ramp) to screen readers via one spoken label.

type Props = {
  theme: UiTheme;
  projection: ProgramTargetProjection;
  unitsPreference: UnitsPreference;
  // Muscle / exercise display name for a target (the editor's keyLabel).
  labelFor: (item: TargetItem) => string;
  // Localized short metric label ("hard sets" / "tonnage").
  metricLabel: (metric: TargetMetric) => string;
};

const targetKey = (item: TargetItem): string =>
  `${item.axis}:${item.key}:${item.metric}`;

export function TargetProjectionMatrix({
  theme,
  projection,
  unitsPreference,
  labelFor,
  metricLabel,
}: Props) {
  const { t, i18n } = useTranslation();
  const unitLabels = weightUnitLabels(t);
  const { wide } = useBreakpoint();
  const locale = i18n.resolvedLanguage ?? "en-US";
  const styles = makeStyles(theme);
  const { weekCount, sessionNames, targets } = projection;

  const [week, setWeek] = useState(0);
  // Keyed by target identity, not row index, so removing/reordering a target
  // mid-edit never opens the wrong row.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const sel = Math.min(week, weekCount - 1);
  const hasRamp = weekCount > 1;

  const fmt = (v: number, metric: TargetMetric): string =>
    formatTargetMetricValue(v, metric, unitsPreference, locale, unitLabels);
  // A day cell: empty day reads "–", real values format by metric.
  const dayText = (v: number, metric: TargetMetric): string =>
    v > 0 ? fmt(v, metric) : "–";

  const rampText = (tgt: ProjectedTarget): string =>
    `${fmt(tgt.weekTotals[0]!, tgt.item.metric)} → ${fmt(
      tgt.weekTotals[weekCount - 1]!,
      tgt.item.metric,
    )}`;

  // The whole row spoken as one label, so a screen reader hears the per-day
  // distribution and the ramp — the two things this matrix exists to show —
  // instead of only the collapsed total.
  const spokenLabel = (tgt: ProjectedTarget): string => {
    const metric = tgt.item.metric;
    const days = tgt.grid[sel]!
      .map((v, d) => (v > 0 ? `${sessionNames[d]} ${fmt(v, metric)}` : null))
      .filter(Boolean)
      .join(", ");
    const total = `${zoneWord(t, tgt.weekZones[sel]!)} ${fmt(tgt.weekTotals[sel]!, metric)}`;
    const ramp = hasRamp
      ? ` · ${t("composer.projectionRamp", { defaultValue: "Cycle" })} ${rampText(tgt)}`
      : "";
    return `${labelFor(tgt.item)} · ${metricLabel(metric)} · ${days} · ${total}${ramp}`;
  };

  const zoneCell = (tgt: ProjectedTarget) => {
    const zone = tgt.weekZones[sel]!;
    return (
      <View style={styles.zone}>
        <Text style={[styles.zoneGlyph, zoneTextStyle(theme, zone)]}>
          {ZONE_GLYPH[zone]}
        </Text>
        <Text style={[styles.totalValue, zoneTextStyle(theme, zone)]}>
          {fmt(tgt.weekTotals[sel]!, tgt.item.metric)}
        </Text>
      </View>
    );
  };

  const rowInner = (tgt: ProjectedTarget) => {
    const metric = tgt.item.metric;
    return (
      <>
        <View style={[styles.labelCol, wide && styles.labelColWide]}>
          <Text style={styles.rowLabel} numberOfLines={2}>
            {labelFor(tgt.item)}
          </Text>
          <Text style={styles.rowMetric}>{metricLabel(metric)}</Text>
        </View>
        {wide
          ? tgt.grid[sel]!.map((v, d) => (
              <Text key={d} style={[styles.dayCol, styles.dayValue]}>
                {dayText(v, metric)}
              </Text>
            ))
          : null}
        <View style={styles.totalCol}>{zoneCell(tgt)}</View>
        {hasRamp ? (
          <Text style={[styles.rampCol, styles.rampValue]}>{rampText(tgt)}</Text>
        ) : null}
      </>
    );
  };

  // Column header — day labels only where the day columns render (wide).
  const headRow = (
    <View style={styles.headRow}>
      <Text style={[styles.headCell, styles.labelCol, wide && styles.labelColWide]}>
        {t("composer.projectionTarget", { defaultValue: "Target" })}
      </Text>
      {wide
        ? sessionNames.map((name, d) => (
            <Text key={d} style={[styles.headCell, styles.dayCol]} numberOfLines={1}>
              {name}
            </Text>
          ))
        : null}
      <Text style={[styles.headCell, styles.totalCol]}>
        {t("composer.projectionWeekTotal", { defaultValue: "Week" })}
      </Text>
      {hasRamp ? (
        <Text style={[styles.headCell, styles.rampCol]}>
          {t("composer.projectionRamp", { defaultValue: "Cycle" })}
        </Text>
      ) : null}
    </View>
  );

  const rows = targets.map((tgt) => {
    const k = targetKey(tgt.item);
    const isOpen = expandedKey === k;
    return (
      <View key={k} style={styles.rowGroup}>
        {wide ? (
          <View style={styles.row} accessible accessibilityLabel={spokenLabel(tgt)}>
            {rowInner(tgt)}
          </View>
        ) : (
          <Pressable
            style={styles.row}
            onPress={() => setExpandedKey(isOpen ? null : k)}
            accessibilityRole="button"
            accessibilityState={{ expanded: isOpen }}
            {...stateAria({ expanded: isOpen })}
            accessibilityLabel={spokenLabel(tgt)}
          >
            {rowInner(tgt)}
          </Pressable>
        )}

        {/* Narrow: the tapped row expands to its per-day breakdown. */}
        {!wide && isOpen ? (
          <View style={styles.dayChips}>
            {tgt.grid[sel]!.map((v, d) => (
              <View
                key={d}
                style={styles.dayChip}
                accessibilityLabel={`${sessionNames[d]}: ${
                  v > 0
                    ? fmt(v, tgt.item.metric)
                    : t("composer.projectionEmpty", { defaultValue: "Not scheduled" })
                }`}
              >
                <Text style={styles.dayChipName}>{sessionNames[d]}</Text>
                <Text style={styles.dayChipValue}>{dayText(v, tgt.item.metric)}</Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );
  });

  return (
    <View style={styles.wrap}>
      <Text style={styles.title}>
        {t("composer.projectionTitle", { defaultValue: "How targets land" })}
      </Text>

      {weekCount > 1 ? (
        <View style={styles.weeks} accessibilityRole="tablist">
          {Array.from({ length: weekCount }, (_, w) => (
            <Chip
              key={w}
              theme={theme}
              shape="box"
              selected={w === sel}
              label={t("composer.projectionWeekShort", {
                n: w + 1,
                defaultValue: programRoundLabel(w + 1),
              })}
              onPress={() => setWeek(w)}
              accessibilityLabel={t("composer.projectionWeek", {
                n: w + 1,
                defaultValue: "Week {{n}}",
              })}
              style={styles.weekChip}
            />
          ))}
        </View>
      ) : null}

      <Text style={styles.context}>
        {t("composer.projectionContext", {
          n: sel + 1,
          defaultValue: "Sets / load the program prescribes in week {{n}}.",
        })}
      </Text>

      {/* DS-15 — at the wide breakpoint the row is label + one column per
          training day + week total + ramp; with enough days that overshoots
          the viewport (sfonda 768–900px) before it ever gets a chance to
          wrap. A horizontal ScrollView lets it scroll instead of clipping.
          Narrow never hits this: no day columns, so nothing to wrap. */}
      {wide ? (
        <ScrollView horizontal>
          <View>
            {headRow}
            {rows}
          </View>
        </ScrollView>
      ) : (
        <>
          {headRow}
          {rows}
        </>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s2, paddingTop: s.s3, borderTopWidth: 1, borderTopColor: p.border },
    title: { color: p.ink, fontSize: t.sizes.base, fontWeight: "700" },
    weeks: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
    weekChip: { minWidth: 44 },
    context: { color: p.ink3, fontSize: t.sizes.sm },
    headRow: { flexDirection: "row", alignItems: "flex-end", gap: s.s2, paddingTop: s.s1 },
    headCell: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      textTransform: "uppercase",
    },
    rowGroup: { gap: s.s1 },
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s2,
      minHeight: 44,
      paddingVertical: s.s1,
      borderTopWidth: 1,
      borderTopColor: p.border,
    },
    labelCol: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 88 },
    // Inside the wide-breakpoint horizontal ScrollView the row's own width is
    // no longer bounded by the viewport, so flexGrow has nothing to grow
    // into. minWidth (not a fixed width) so a long DE/RU label can still push
    // the column wider instead of being squeezed into two cramped lines —
    // there is room to grow into, the content width is unbounded in here.
    labelColWide: { flexGrow: 0, flexShrink: 0, minWidth: 140 },
    rowLabel: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    rowMetric: { color: p.ink3, fontSize: t.sizes.sm },
    dayCol: { width: 54, textAlign: "right" },
    dayValue: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    totalCol: { minWidth: 92, alignItems: "flex-end" },
    zone: { flexDirection: "row", alignItems: "center", gap: 4 },
    zoneGlyph: { fontSize: t.sizes.base, fontWeight: "700" },
    totalValue: {
      fontSize: t.sizes.base,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    rampCol: { width: 116, textAlign: "right" },
    rampValue: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    dayChips: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
      paddingBottom: s.s2,
      paddingLeft: s.s2,
    },
    dayChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 5,
      paddingHorizontal: s.s2,
      paddingVertical: 4,
      borderRadius: 8,
      backgroundColor: p.surface2,
    },
    dayChipName: { color: p.ink3, fontSize: t.sizes.sm },
    dayChipValue: {
      color: p.ink,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
  });
}
